import { Center, Loader } from '@mantine/core'

const _flags = {
    sales_channels: false,
    analytics: false,
    cart: true,
    account: true,
    anonymous_cart: true,
}

import { createContext, useEffect, useState } from 'react'
import { z } from 'zod'

type FeatureFlag =
    | 'sales_channels'
    | 'analytics'
    | 'cart'
    | 'account'
    | 'anonymous_cart'

type FeatureFlagContextType = {
    flags: Record<FeatureFlag, boolean> | null
    isFeatureEnabled: (flag: FeatureFlag) => boolean
}

const initialContext: FeatureFlagContextType = {
    flags: null,
    isFeatureEnabled: () => false,
}

export const FeatureFlagContext =
    createContext<FeatureFlagContextType>(initialContext)

type FeatureFlagProviderProps = {
    children: React.ReactNode
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
    const [featureFlags, setFeatureFlags] =
        useState<FeatureFlagContextType['flags']>(null)

    const isFeatureEnabled = (flag: FeatureFlag) =>
        !!featureFlags ? featureFlags[flag] : false

    useEffect(() => {
        if (!_flags) return

        const validated = z
            .object({
                sales_channels: z.boolean(),
                analytics: z.boolean(),
                cart: z.boolean(),
                account: z.boolean(),
                anonymous_cart: z.boolean(),
            })
            .safeParse(_flags)

        if (!validated.success) {
            throw new Error('Cannot parse feature flags.')
        }

        setFeatureFlags(validated.data)
    }, [])

    if (!featureFlags) {
        return (
            <Center h="100%">
                <Loader />
            </Center>
        )
    }

    return (
        <FeatureFlagContext.Provider
            value={{
                flags: featureFlags,
                isFeatureEnabled,
            }}
        >
            {children}
        </FeatureFlagContext.Provider>
    )
}
